import { useEffect, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { suggestionAPI } from '../services/api';
import { Lightbulb, Trash2, ExternalLink, User, Mail, Calendar } from 'lucide-react';
import { Button } from './ui/button';
import { Textarea } from './ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { toast } from 'sonner';

const STATUSES = ['all', 'new', 'reviewed', 'planned', 'done', 'dismissed'];
const CATEGORIES = ['all', 'general', 'feature', 'bug', 'ui_ux', 'content', 'other'];

const STATUS_STYLES = {
	new: 'bg-[#1A3C34] text-white',
	reviewed: 'bg-[#8B4513] text-white',
	planned: 'bg-[#5C7CFA] text-white',
	done: 'bg-[#2F855A] text-white',
	dismissed: 'bg-[#9B2C2C] text-white',
};

export default function SuggestionsAdminManager() {
	const { t } = useTranslation();
	const [items, setItems] = useState([]);
	const [counts, setCounts] = useState({ all: 0 });
	const [loading, setLoading] = useState(true);
	const [statusFilter, setStatusFilter] = useState('all');
	const [categoryFilter, setCategoryFilter] = useState('all');
	const [editingNotes, setEditingNotes] = useState({}); // { id: text }

	const reload = useCallback(async () => {
		setLoading(true);
		try {
			const params = {};
			if (statusFilter !== 'all') params.status = statusFilter;
			if (categoryFilter !== 'all') params.category = categoryFilter;
			const r = await suggestionAPI.listAdmin(params);
			setItems(r.data?.items || []);
			setCounts(r.data?.counts || { all: 0 });
		} catch {
			toast.error(t('common.error'));
		}
		setLoading(false);
	}, [statusFilter, categoryFilter, t]);

	useEffect(() => { reload(); }, [reload]);

	const updateStatus = async (id, status) => {
		try {
			await suggestionAPI.update(id, { status });
			toast.success(t('common.success'));
			reload();
		} catch {
			toast.error(t('common.error'));
		}
	};

	const saveNotes = async (id) => {
		const notes = editingNotes[id] ?? '';
		try {
			await suggestionAPI.update(id, { admin_notes: notes });
			toast.success(t('common.success'));
			setEditingNotes((prev) => {
				const next = { ...prev };
				delete next[id];
				return next;
			});
			reload();
		} catch {
			toast.error(t('common.error'));
		}
	};

	const remove = async (id) => {
		if (!window.confirm(t('admin.suggestion_delete_confirm'))) return;
		try {
			await suggestionAPI.remove(id);
			toast.success(t('common.success'));
			reload();
		} catch {
			toast.error(t('common.error'));
		}
	};

	return (
		<div className="space-y-4" data-testid="admin-suggestions-manager">
			{/* Toolbar */}
			<div className="flex items-center justify-between gap-3 flex-wrap">
				<div className="flex items-center gap-2 text-sm text-[#666055]">
					<Lightbulb className="w-4 h-4 text-[#8B4513]" />
					{counts.all} {t('admin.suggestions')}
				</div>
				<div className="flex items-center gap-2 flex-wrap">
					<Select value={statusFilter} onValueChange={setStatusFilter}>
						<SelectTrigger className="bg-white border-[#E6E2D6] rounded-sm h-9 text-xs w-44"
													 data-testid="admin-suggestions-status-filter">
							<SelectValue />
						</SelectTrigger>
						<SelectContent>
							{STATUSES.map((s) => (
								<SelectItem key={s} value={s}>
									{t(`admin.suggestion_status_${s}`)} ({counts[s] ?? 0})
								</SelectItem>
							))}
						</SelectContent>
					</Select>
					<Select value={categoryFilter} onValueChange={setCategoryFilter}>
						<SelectTrigger className="bg-white border-[#E6E2D6] rounded-sm h-9 text-xs w-44"
													 data-testid="admin-suggestions-category-filter">
							<SelectValue />
						</SelectTrigger>
						<SelectContent>
							{CATEGORIES.map((c) => (
								<SelectItem key={c} value={c}>{t(`suggestions.cat_${c === 'all' ? 'all' : c}`)}</SelectItem>
							))}
						</SelectContent>
					</Select>
				</div>
			</div>

			{loading ? (
				<div className="text-sm text-[#666055]">Loading...</div>
			) : items.length === 0 ? (
				<div className="bg-white border border-[#E6E2D6] rounded-sm p-12 text-center text-sm text-[#666055]"
						 data-testid="admin-suggestions-empty">
					{t('admin.no_suggestions')}
				</div>
			) : (
				<div className="space-y-3">
					{items.map((s) => {
						const isEditingNotes = editingNotes[s.id] !== undefined;
						return (
							<div
								key={s.id}
								className="bg-white border border-[#E6E2D6] rounded-sm p-4"
								data-testid={`admin-suggestion-row-${s.id}`}
							>
								<div className="flex items-center gap-2 flex-wrap mb-2">
                  <span className={`px-2 py-0.5 text-[10px] uppercase tracking-wider font-semibold rounded-sm 
                  		${STATUS_STYLES[s.status] || 'bg-[#E6E2D6] text-[#666055]'}`}>
                    {t(`admin.suggestion_status_${s.status}`)}
                  </span>
									<span className="bg-[#F2F0E9] text-[#666055] px-2 py-0.5 text-[10px] uppercase tracking-wider
											font-semibold rounded-sm">
                    {t(`suggestions.cat_${s.category}`)}
                  </span>
									<span className="text-[10px] text-[#666055]/70 inline-flex items-center gap-1 ml-auto">
                    <Calendar className="w-3 h-3" /> {(s.created_at || '').slice(0, 16).replace('T', ' ')}
                  </span>
								</div>

								<p className="text-sm text-[#1A3C34] whitespace-pre-line mb-3"
									 data-testid={`admin-suggestion-message-${s.id}`}>
									{s.message}
								</p>

								<div className="flex items-center gap-3 flex-wrap text-[11px] text-[#666055] mb-3">
									{s.user_id ? (
										<span className="inline-flex items-center gap-1">
                      <User className="w-3 h-3" /> {s.user_name || s.contact_email || s.user_id.slice(0, 8)}
                    </span>
									) : (
										<span className="inline-flex items-center gap-1 italic">
                      <User className="w-3 h-3" /> {t('admin.suggestion_anonymous')}
                    </span>
									)}
									{s.contact_email && (
										<a
											href={`mailto:${s.contact_email}?subject=Re: your suggestion on Equi-Market`}
											className="inline-flex items-center gap-1 text-[#1A3C34] hover:underline"
											data-testid={`admin-suggestion-email-${s.id}`}
										>
											<Mail className="w-3 h-3" /> {s.contact_email}
											<ExternalLink className="w-2.5 h-2.5" />
										</a>
									)}
								</div>

								{s.admin_notes && !isEditingNotes && (
									<div className="bg-[#F2F0E9] border-l-2 border-[#8B4513] px-3 py-2 mb-3 text-xs
											text-[#3a3a3a] whitespace-pre-line" data-testid={`admin-suggestion-notes-${s.id}`}>
                    <span className="font-semibold text-[#8B4513] uppercase tracking-wider text-[10px] block mb-1">
                      {t('admin.suggestion_notes')}
                    </span>
										{s.admin_notes}
									</div>
								)}

								{isEditingNotes && (
									<div className="mb-3">
										<Textarea
											value={editingNotes[s.id]}
											onChange={(e) => setEditingNotes({ ...editingNotes, [s.id]: e.target.value })}
											maxLength={2000}
											rows={3}
											placeholder={t('admin.suggestion_notes_placeholder')}
											className="bg-[#F9F7F2] border-[#E6E2D6] rounded-sm text-sm"
											data-testid={`admin-suggestion-notes-input-${s.id}`}
										/>
										<div className="flex justify-end gap-2 mt-2">
											<Button
												size="sm"
												variant="ghost"
												onClick={() => setEditingNotes((prev) => { const n = { ...prev }; delete n[s.id]; return n; })}
												className="text-[#666055] h-8 text-xs"
											>
												{t('common.cancel')}
											</Button>
											<Button
												size="sm"
												onClick={() => saveNotes(s.id)}
												className="bg-[#1A3C34] hover:bg-[#153029] text-white rounded-sm h-8 text-xs"
												data-testid={`admin-suggestion-notes-save-${s.id}`}
											>
												{t('common.save')}
											</Button>
										</div>
									</div>
								)}

								<div className="flex items-center gap-2 flex-wrap pt-2 border-t border-[#E6E2D6]">
									<Select value={s.status} onValueChange={(v) => updateStatus(s.id, v)}>
										<SelectTrigger
											className="bg-white border-[#E6E2D6] rounded-sm h-8 text-xs w-36"
											data-testid={`admin-suggestion-status-${s.id}`}
										>
											<SelectValue />
										</SelectTrigger>
										<SelectContent>
											{['new', 'reviewed', 'planned', 'done', 'dismissed'].map((st) => (
												<SelectItem key={st} value={st}>{t(`admin.suggestion_status_${st}`)}</SelectItem>
											))}
										</SelectContent>
									</Select>
									{!isEditingNotes && (
										<Button
											size="sm"
											variant="outline"
											onClick={() => setEditingNotes({ ...editingNotes, [s.id]: s.admin_notes || '' })}
											className="border-[#E6E2D6] text-[#1A3C34] rounded-sm h-8 text-xs"
											data-testid={`admin-suggestion-edit-notes-${s.id}`}
										>
											{s.admin_notes ? t('admin.suggestion_edit_notes') : t('admin.suggestion_add_notes')}
										</Button>
									)}
									<Button
										size="sm"
										variant="outline"
										onClick={() => remove(s.id)}
										className="border-[#E6E2D6] text-[#9B2C2C] rounded-sm h-8 w-8 p-0 ml-auto"
										title={t('common.delete')}
										data-testid={`admin-suggestion-delete-${s.id}`}
									>
										<Trash2 className="w-3 h-3" />
									</Button>
								</div>
							</div>
						);
					})}
				</div>
			)}
		</div>
	);
}